import { useState, useEffect, useCallback } from 'react';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  actor_id: string | null;
  created_at: string;
  is_read?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || '/api';

export default function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // SSE-over-HTTP/2 through CF Workers was flaky (ERR_QUIC_PROTOCOL_ERROR,
  // ERR_HTTP2_PROTOCOL_ERROR in console). Swapped to short polling — less
  // clever, much more reliable. 20s cadence keeps the notification bell
  // feeling live without pegging the DB.
  const [connected] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const [listRes, unreadRes] = await Promise.all([
          fetch(`${API_URL}/social/notifications?limit=20`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_URL}/social/notifications/count`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => null),
        ]);
        if (listRes.ok) {
          const data = await listRes.json();
          if (data?.notifications) setNotifications(data.notifications);
        }
        if (unreadRes?.ok) {
          const d = await unreadRes.json();
          if (typeof d?.count === 'number') setUnreadCount(d.count);
        }
      } catch { /* noop */ }
    };

    poll(); // kick off immediately
    const interval = setInterval(poll, 20_000);
    // Also refresh when the tab becomes visible (catches the case where
    // the tab was backgrounded for minutes — intervals throttle there).
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const markRead = useCallback(async (notifId: string) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setNotifications(prev => prev.map(n =>
      n.id === notifId ? { ...n, is_read: true, isRead: true } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await fetch(`${API_URL}/social/notifications/${notifId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* */ }
  }, []);

  const markAllRead = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true, isRead: true })));
    setUnreadCount(0);
    try {
      await fetch(`${API_URL}/social/notifications/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* */ }
  }, []);

  return { unreadCount, notifications, connected, markRead, markAllRead };
}
